import React, { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import {
  AuthCallbackType,
  Error,
  Instance,
  Member,
  Organization,
  Role,
  SigninCredentialsPayload,
  SignupCredentialsPayload,
  VerifySessionResponse,
} from '@ui/interfaces'
import { authService } from '@ui/services'
import { useInstanceStore } from '@ui/stores'

interface AuthContextType {
  currentMember: Member | null
  currentOrganization: Organization | null
  currentInstance: Instance | null
  isDemoMode: boolean
  signinWithPassword: (data: SigninCredentialsPayload, callback: AuthCallbackType) => void
  signupWithPassword: (data: SignupCredentialsPayload, callback: AuthCallbackType) => void
  signout: (callback: AuthCallbackType) => void
  resetPassword: (email: string, callback: AuthCallbackType) => void
  setPassword: (password: string, passwordConfirmation: string, token: string, callback: AuthCallbackType) => void
  invited: (password: string, passwordConfirmation: string, token: string, callback: AuthCallbackType) => void
  confirmEmail: (token: string, isDemo: boolean, callback: AuthCallbackType) => void
  setOrganization: (id: number, callback: AuthCallbackType) => void
  restoreSession: (callback: VoidFunction) => void
  updateRole: (userId: number, organizationId: number, role: Role) => Promise<Member>
}

const AuthContext = React.createContext<AuthContextType>(null!)

export function useAuth() {
  return React.useContext(AuthContext)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentMember, setCurrentMember] = React.useState<Member | null>(null)
  const [currentOrganization, setCurrentOrganization] = React.useState<Organization | null>(null)
  const [currentInstance, setCurrentInstance] = React.useState<Instance | null>(null)

  const { instances, selectedInstanceKey, setSelectedInstanceKey } = useInstanceStore(
    useShallow((state) => ({
      instances: state.instances,
      selectedInstanceKey: state.selectedInstanceKey,
      setSelectedInstanceKey: state.setSelectedInstanceKey,
    }))
  )

  // Make sure we update the current instance of the provider when the selected instance key changes
  // navigations and manage are dependent on this
  useEffect(() => {
    if (selectedInstanceKey) {
      const instance = instances.find((instance) => instance.connect_key === selectedInstanceKey)
      if (instance) setCurrentInstance(instance)
    }
  }, [selectedInstanceKey])

  useEffect(() => {
    const validateSessionInterval = setInterval(async () => {
      try {
        const validSession = await authService.validateSession()
        if (!validSession || validSession.logged_in === false) {
          clearInterval(validateSessionInterval) // Stop the interval check
          signout((err: Error | null) => {
            if (err) console.error('could not sign out', err)
          })
        }
      } catch (err) {
        clearInterval(validateSessionInterval) // Stop the interval check
        signout((err) => {
          if (err) console.error('could not sign out', err)
        })
      }
    }, 60000)

    return () => clearInterval(validateSessionInterval)
  }, [])

  const signinWithPassword = (data: SigninCredentialsPayload, callback: AuthCallbackType) => {
    return authService.signinWithPassword(data, (err: Error | null, response?: VerifySessionResponse) => {
      setOrganizationAndMember(response)
      callback(err, response)
    })
  }

  const signupWithPassword = (data: SignupCredentialsPayload, callback: AuthCallbackType) => {
    return authService.signupWithPassword(data, (err: Error | null, response?: VerifySessionResponse) => {
      // We do not call setOrganizationAndMember here because
      // the user needs to verify their email before going through the
      // organization setup process
      callback(err, response)
    })
  }

  const signout = (callback: AuthCallbackType) => {
    return authService.signout((err: Error | null) => {
      setCurrentMember(null)
      setCurrentOrganization(null)
      setCurrentInstance(null)
      authService.validateSession()
      if (err) callback(err)
      else callback(null)
    })
  }

  const resetPassword = (email: string, callback: AuthCallbackType) => {
    return authService.resetPassword(email, callback)
  }

  const setPassword = (password: string, passwordConfirmation: string, token: string, callback: AuthCallbackType) => {
    return authService.setPassword(
      password,
      passwordConfirmation,
      token,
      (err: Error | null, response?: VerifySessionResponse) => {
        setOrganizationAndMember(response)
        callback(err, response)
      }
    )
  }

  const invited = (password: string, passwordConfirmation: string, token: string, callback: AuthCallbackType) => {
    return authService.invited(
      password,
      passwordConfirmation,
      token,
      (err: Error | null, response?: VerifySessionResponse) => {
        setOrganizationAndMember(response)
        callback(err, response)
      }
    )
  }

  const confirmEmail = (token: string, isDemo: boolean, callback: AuthCallbackType) => {
    return authService.confirmEmail(token, isDemo, (err: Error | null, response?: VerifySessionResponse) => {
      setOrganizationAndMember(response)
      callback(err, response)
    })
  }

  const setOrganization = async (id: number, callback: AuthCallbackType) => {
    await authService.setOrganization(id, (err: Error | null, response?: VerifySessionResponse) => {
      setOrganizationAndMember(response)
      callback(err, response)
    })

    await authService.validateSession()
  }

  const restoreSession = async (callback: AuthCallbackType) => {
    if (!currentMember) {
      const sessionData = await authService.validateSession()

      if (sessionData?.member && sessionData?.organization) {
        setCurrentMember(sessionData.member)
        setCurrentOrganization(sessionData.organization)
        setCurrentInstance(sessionData.instance)

        setSelectedInstanceKey(sessionData.instance?.connect_key)

        authService.isAuthenticated = true // Update authentication flag
      } else if (sessionData?.member && !sessionData?.organization) {
        setCurrentMember(sessionData.member)
        authService.isAuthenticated = true
      } else {
        authService.isAuthenticated = false // Update authentication flag
      }
    }
    callback(null)
  }

  const setOrganizationAndMember = (response: VerifySessionResponse | undefined) => {
    if (response) {
      setCurrentMember(response.member)
      setCurrentOrganization(response.organization)
      setCurrentInstance(response?.instance)
      setSelectedInstanceKey(response?.instance?.connect_key)
    }
  }

  const isDemoMode = currentOrganization?.slug === 'datable-demo' && !currentMember?.is_datable_admin

  const value = {
    currentMember: currentMember,
    currentOrganization: currentOrganization,
    currentInstance: currentInstance,
    isDemoMode,
    signinWithPassword,
    signupWithPassword,
    signout,
    restoreSession,
    resetPassword,
    setPassword,
    invited,
    setOrganization,
    confirmEmail,
    updateRole: authService.updateRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
